import {
  CalendarClockIcon,
  PhoneIncomingIcon,
  PhoneMissedIcon,
  PhoneOutgoingIcon,
  VoicemailIcon,
} from "lucide-react";

import { Center } from "@/components/Center";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";
import { CallDirection } from "@/types";

type CallIconState =
  | "inbound"
  | "outbound"
  | "missed"
  | "voicemail"
  | "scheduled";

const stateToIcon = {
  inbound: PhoneIncomingIcon,
  missed: PhoneMissedIcon,
  outbound: PhoneOutgoingIcon,
  scheduled: CalendarClockIcon,
  voicemail: VoicemailIcon,
};

// Statuses (quo CallStatus or voice-AI outreach) that count as a call that
// never connected. Normalized so "no-answer" / "NO_ANSWER" both match.
const MISSED_CALL_STATUSES = new Set([
  "ABANDONED",
  "BUSY",
  "CANCELED",
  "CANCELLED",
  "FAILED",
  "MISSED",
  "NO_ANSWER",
  "REJECTED",
]);

export const CallIcon = ({
  direction,
  status,
  isAi = false,
  isMissed = false,
  isVoicemail = false,
  isScheduled = false,
  hasTooltip = true,
  className,
  circleClassName,
}: {
  // AI calls have no direction — they default to outbound.
  direction?: CallDirection;
  // Raw call status; missed detection is handled here.
  status?: string | null;
  isAi?: boolean;
  // Explicit overrides for callers that derive missed/voicemail heuristically.
  isMissed?: boolean;
  isVoicemail?: boolean;
  // A call booked for later that hasn't been placed yet.
  isScheduled?: boolean;
  // Off for generic uses (e.g. "all staffer calls" tiles) where a
  // status/direction label would mislead.
  hasTooltip?: boolean;
  className?: string;
  circleClassName?: string;
}) => {
  /* Helpers */
  const getState = (): CallIconState => {
    const normalizedStatus = (status ?? "")
      .toUpperCase()
      .replace(/[\s-]+/g, "_");
    if (isMissed || MISSED_CALL_STATUSES.has(normalizedStatus)) {
      return "missed";
    }
    if (isScheduled) return "scheduled";
    if (isVoicemail) return "voicemail";
    if (direction === CallDirection.INCOMING) return "inbound";
    return "outbound";
  };

  const getLabel = (state: CallIconState): string => {
    const actor = isAi ? "AI" : "staffer";
    if (state === "voicemail")
      return isAi ? "AI voicemail" : "Staffer voicemail";
    if (state === "missed") return `Missed ${actor} call`;
    if (state === "scheduled") return `Scheduled ${actor} call`;
    if (state === "inbound") return `Inbound ${actor} call`;
    return `Outbound ${actor} call`;
  };

  /* Render */
  const render = () => {
    const state = getState();
    const Icon = stateToIcon[state];

    // Grey circle for human calls; gradient ring for AI calls (matches the
    // Figma circle asset).
    const circle = (
      <Center
        className={cn(
          "h-8 w-8 shrink-0 rounded-full",
          isAi
            ? "border border-[#CEE7FE] bg-[linear-gradient(286deg,#CEE0FF_0%,#FAFDFF_100%)] text-primary-900"
            : "bg-slate-100 text-slate-600",
          circleClassName,
        )}
      >
        <Icon
          className={cn(
            "h-3.5 w-3.5 shrink-0",
            className,
            state === "missed" && "text-red-500",
          )}
        />
      </Center>
    );

    if (!hasTooltip) return circle;

    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="shrink-0">{circle}</span>
          </TooltipTrigger>
          <TooltipContent>{getLabel(state)}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return render();
};
